// Credit balance + plan tier hook. Read-only from the frontend (mutations
// happen via backend RPCs); we fetch the profile from the backend (which
// queries Cloud SQL since Phase 2) and provide a manual `refresh()` for
// after API ops that affect the balance.
//
// Pre-Phase-2 this hook called supabase.from('user_profile').select(...)
// directly under RLS. That path stayed live AFTER the Cloud SQL swap and
// silently cost every page mount ~30s (Supabase rate limit / stale RLS),
// so the entire app felt broken. Now routed through GET /api/user-profile.
//
// Provider is mounted inside AuthProvider in App.tsx so `useAuth` works.

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { API_BASE, getAuthHeaders } from '@/lib/api';
import { useAuth } from './useAuth';

export type PlanTier = 'free' | 'starter' | 'pro' | 'business';

interface CreditsContextType {
  /** null while loading or if user has no profile row */
  balance: number | null;
  planTier: PlanTier;
  /** ISO 8601 timestamp when the user's subscription will end. Set by the
   *  Stripe webhook when user cancels via Customer Portal with "at end of
   *  billing period" (the default). null = active or never subscribed. */
  subscriptionEndsAt: string | null;
  /** ISO 8601 timestamp when the user's free trial ends. Non-null AND in the
   *  future ⇒ the user is currently trialing → show a "Trial" badge. Null once
   *  the trial converts to a paid cycle or the subscription is cancelled. */
  trialEndsAt: string | null;
  loading: boolean;
  /** True ONLY after a SUCCESSFUL profile fetch for the current user. Stays
   *  false while logged out, during the login transition, and if all fetch
   *  retries fail. Consumers that gate access (AuthGate's plan gate) must check
   *  this — planTier defaults to 'free' in all the non-confirmed cases, so
   *  gating on planTier alone would bounce a PAYING user on a transient failure
   *  or the logged-out→logged-in race. Gate only on `loaded && planTier==='free'`. */
  loaded: boolean;
  /** Re-fetch from Supabase. Call after any API op that affects credits. */
  refresh: () => Promise<void>;
}

const CreditsContext = createContext<CreditsContextType | null>(null);

export function CreditsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // Depend on the user ID (stable string), NOT the user object reference.
  // Supabase's onAuthStateChange fires several times per page-load (INITIAL_
  // SESSION + SIGNED_IN + TOKEN_REFRESHED) and each call to setUser({...same
  // data}) creates a new object reference. With [user] as the dep below,
  // refresh would rebuild on every fire and the useEffect would re-trigger
  // → /api/user-profile called 3-5× per page load. Keyed on user?.id, it
  // only fires when the actual signed-in user changes.
  const userId = user?.id ?? null;
  const [balance, setBalance] = useState<number | null>(null);
  const [planTier, setPlanTier] = useState<PlanTier>('free');
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState<string | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      setBalance(null);
      setPlanTier('free');
      setSubscriptionEndsAt(null);
      setTrialEndsAt(null);
      setLoaded(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Retry a few times before giving up. On a fresh FIRST login the profile
    // call can transiently fail — a Cloud Run cold start (min-instances=0 →
    // ~5-15s first request), a not-yet-minted Firebase token, or a 403
    // invite_required racing ahead of InviteLayer's code redeem. Without the
    // retry, that single blip left balance=null and the credits chip read "0"
    // even though the user actually HAS credits (the reported "new user shows 0
    // credits" bug — the DB grant was there the whole time). A warm backend +
    // valid session succeeds on attempt 1 → zero added latency. The
    // sp:invite-redeemed listener below is the backstop for a slow redeem.
    const MAX_ATTEMPTS = 4;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(`${API_BASE}/api/user-profile`, {
          headers: await getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          setBalance(typeof data.credits_balance === 'number' ? data.credits_balance : null);
          setPlanTier((data.plan_tier as PlanTier) ?? 'free');
          setSubscriptionEndsAt(data.subscription_ends_at ?? null);
          setTrialEndsAt(data.trial_ends_at ?? null);
          setLoaded(true);   // CONFIRMED profile — plan gate may now trust planTier
          setLoading(false);
          return;
        }
      } catch {
        // network error — fall through to backoff/retry
      }
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, Math.min(3200, 400 * 2 ** attempt)));
      }
    }
    // All attempts failed. Defensive defaults — the credits chip shows no
    // balance until the sp:invite-redeemed listener (or a manual op) re-fetches.
    setBalance(null);
    setPlanTier('free');
    setSubscriptionEndsAt(null);
    setTrialEndsAt(null);
    setLoaded(false);   // NOT confirmed — gate must fail open, never bounce a paid user on a failed fetch
    setLoading(false);
  }, [userId]);

  // Auto-refresh when the signed-in user changes (login, logout, fresh load).
  useEffect(() => { refresh(); }, [refresh]);

  // First-login recovery: if /api/user-profile 403'd invite_required before the
  // invite redeem landed (a signup race), balance is stuck at null → the UI
  // reads "0". InviteLayer dispatches sp:invite-redeemed once the code is
  // redeemed — re-fetch then so the real balance appears without a manual
  // reload. Idempotent for an already-loaded balance.
  useEffect(() => {
    const onRedeemed = () => { void refresh(); };
    window.addEventListener('sp:invite-redeemed', onRedeemed);
    return () => window.removeEventListener('sp:invite-redeemed', onRedeemed);
  }, [refresh]);

  return (
    <CreditsContext.Provider value={{ balance, planTier, subscriptionEndsAt, trialEndsAt, loading, loaded, refresh }}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits(): CreditsContextType {
  const ctx = useContext(CreditsContext);
  if (!ctx) throw new Error('useCredits must be used within CreditsProvider');
  return ctx;
}
